import { Clock, Gauge, HelpCircle, History, Plug, PlugZap, Radio, SlidersHorizontal, Sun, Wifi } from 'lucide-react';
import ConnexionBadge from '@/shared/components/ConnexionBadge';
import {
  DECONNECTEE, SANS_CONTACT,
  ageJoignabilite, ancienneteTexte, donneesPerimees, etatJoignabilite,
  horodatageTexte, mesureTexte, vuA,
} from '@/shared/utils/liveness';

/**
 * Relevé d'une batterie sur la fiche détaillée : d'abord quand on l'a vue pour la
 * dernière fois, ensuite ses chiffres.
 *
 * Cet ordre n'est pas décoratif. Les chiffres d'une batterie déconnectée sont les
 * derniers connus : les lire sans savoir de quand ils datent, c'est ce qui a fait
 * republier 240 fois une consigne à une machine qui n'écoutait plus. Un chiffre périmé
 * est donc grisé et daté, et un chiffre absent s'écrit « inconnu », jamais 0.
 */
export default function BatteryReadout({ asset }) {
  const etat = etatJoignabilite(asset);
  const age = ageJoignabilite(asset);
  // Pas de repli sur la date de la base quand aucun contact n'est enregistré : ce champ
  // date alors de la création de l'actif, l'afficher donnerait une date crédible et
  // fausse, précisément ce que cet écran est censé faire disparaître.
  const derniereTrame = vuA(asset)
    || (etat === SANS_CONTACT ? null : asset.dernier_contact);
  const perime = donneesPerimees(asset) || etat === DECONNECTEE;
  const sansContact = etat === SANS_CONTACT;

  return (
    <div
      className="p-4 rounded-lg border border-neutral-200/80 bg-white space-y-3"
      data-testid="battery-readout"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[13px] font-medium text-neutral-900">État de la batterie</h3>
        <ConnexionBadge etat={etat} ageS={age} testId="battery-liveness-badge" />
      </div>

      {/* En tête, toujours : l'heure du dernier signe de vie. */}
      <p className="flex items-center gap-1.5 text-[11px] text-neutral-500" data-testid="battery-last-seen">
        <Clock className="w-3 h-3 flex-shrink-0 text-neutral-400" />
        {derniereTrame ? (
          <span>
            Dernier signe de vie : <span className="font-medium text-neutral-700">
              {horodatageTexte(derniereTrame) || derniereTrame}
            </span>
            {age != null && <span className="text-neutral-400"> · il y a {ancienneteTexte(age)}</span>}
          </span>
        ) : (
          <span>Aucune mesure reçue à ce jour</span>
        )}
      </p>

      {sansContact ? <SansContact /> : (
        <>
          {perime && (
            <p
              className="flex items-start gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-2 py-1.5"
              data-testid="battery-stale-notice"
            >
              <History className="w-3 h-3 mt-0.5 flex-shrink-0 text-amber-600" />
              <span>
                Chiffres ci-dessous : derniers connus
                {horodatageTexte(derniereTrame) ? `, relevés le ${horodatageTexte(derniereTrame)}` : ''}.
                Ce n'est pas l'état actuel de la batterie.
              </span>
            </p>
          )}
          <div className="space-y-1" data-testid="battery-figures">
            <Chiffre label="État" valeur={etatTexte(asset.etat)} perime={perime} />
            <Chiffre label="Charge (SOC)" valeur={mesureTexte(asset.soc_pct, '%')} perime={perime} />
            <Chiffre label="Puissance" valeur={mesureTexte(asset.puissance_kw, 'kW')} perime={perime} />
            <Chiffre label="Tension" valeur={mesureTexte(asset.tension_v, 'V')} perime={perime} />
            <Chiffre label="Température" valeur={mesureTexte(asset.temperature_c, '°C')} perime={perime} />
          </div>
          <ProductionSolaire production={asset.production_pv} />
          <LimitesDeclarees limites={asset.limites} />
          <Alimentation secteur={asset.secteur} />
          <ModePilotage mode={asset.mode_pilotage} />
          <Energie energie={asset.energie} />
          <Signal signal={asset.signal} />
        </>
      )}
    </div>
  );
}

/**
 * Ce que les panneaux produisent.
 *
 * David, le 30/08 : « la centrale qui est connectée via Hoymiles, la data ne remonte
 * pas au front ». Elle remontait jusqu'au serveur et s'arrêtait là. Tant que sa
 * production n'apparaît pas, le tableau de bord sous-estime le parc — et une valeur
 * absente d'un total est plus dangereuse qu'une valeur visiblement fausse, parce que
 * personne ne la remarque.
 *
 * Deux moitiés, deux dates. La puissance arrive chaque seconde, l'énergie du jour
 * toutes les cinq minutes : les dater ensemble ferait passer un chiffre vieux de cinq
 * minutes pour du temps réel.
 *
 * Et surtout, DEUX CHOSES QUI NE SE RESSEMBLENT PAS : « 0 W » veut dire que les
 * panneaux ne produisent rien à cet instant, ce qui est normal la nuit ; « inconnu »
 * veut dire que personne ne mesure. Les afficher pareil laisserait croire à une
 * centrale en panne, ou l'inverse.
 */
function ProductionSolaire({ production }) {
  if (!production) return null;
  const { instantane, jour } = production;
  if (!instantane && !jour) return null;

  const rienDuTout = instantane?.puissance_w == null && jour?.energie_wh == null;

  return (
    <div
      className="rounded-md border border-amber-100 bg-amber-50/40 px-2.5 py-2 text-[11px]"
      data-testid="production-solaire"
    >
      <p className="mb-1 flex items-center gap-1.5 font-medium text-neutral-800">
        <Sun className="h-3.5 w-3.5 flex-shrink-0 text-amber-500" />
        Production solaire
      </p>

      {rienDuTout ? (
        <p className="text-neutral-500" data-testid="production-sans-mesure">
          Aucune mesure de production reçue. Ce n&apos;est pas une production nulle :
          c&apos;est une production qu&apos;on ne connaît pas.
        </p>
      ) : (
        <div className="space-y-0.5">
          <LigneProduction
            label="À l&apos;instant"
            valeur={puissanceTexte(instantane?.puissance_w)}
            bloc={instantane}
            tid="production-instantanee"
          />
          <LigneProduction
            label="Depuis ce matin"
            valeur={energieTexte(jour?.energie_wh)}
            bloc={jour}
            tid="production-jour"
          />
        </div>
      )}

      <TrackersMuets instantane={instantane} jour={jour} />
    </div>
  );
}

/** Une ligne de production, avec sa date et son état de fraîcheur. */
function LigneProduction({ label, valeur, bloc, tid }) {
  const perime = bloc?.donnees_perimees === true;
  const inconnu = valeur === 'inconnu';
  const ton = inconnu ? 'text-neutral-400 italic'
    : perime ? 'text-neutral-400' : 'text-neutral-800';
  return (
    <div data-testid={tid}>
      <div className="flex items-center justify-between">
        <span className="text-neutral-500">{label}</span>
        <span className={`font-medium ${ton}`}>{valeur}</span>
      </div>
      {/* Une valeur sans date ne permet pas de savoir si elle est fraîche. */}
      {bloc?.vu_a && (
        <p className="text-[10px] text-neutral-400">
          {perime ? 'Dernier chiffre connu, relevé' : 'Relevé'} le{' '}
          {horodatageTexte(bloc.vu_a) || bloc.vu_a}
          {bloc.age_s != null && ` · il y a ${ancienneteTexte(bloc.age_s)}`}
          {perime && '. Ce n\u2019est pas l\u2019état actuel.'}
        </p>
      )}
    </div>
  );
}

/**
 * L'aveu d'un total incomplet.
 *
 * La machine porte deux entrées photovoltaïques. Quand l'une se tait, additionner
 * l'autre et présenter le résultat comme la production du site le sous-estime de
 * moitié, en silence. C'est le défaut que ce bloc existe pour rendre visible.
 */
function TrackersMuets({ instantane, jour }) {
  const muets = Math.max(instantane?.trackers_muets || 0, jour?.trackers_muets || 0);
  if (!muets) return null;
  return (
    <p
      className="mt-1 flex items-start gap-1.5 text-[10px] text-amber-800"
      data-testid="production-incomplete"
    >
      <History className="mt-0.5 h-3 w-3 flex-shrink-0 text-amber-600" />
      <span>
        Total incomplet : {muets === 1 ? "une entrée photovoltaïque n'a rien rapporté"
          : `${muets} entrées photovoltaïques n'ont rien rapporté`}.
        La production réelle du site est donc supérieure au chiffre ci-dessus.
      </span>
    </p>
  );
}

/** Des W sous le kilowatt, des kW au-dessus. Une absence s'écrit, elle ne vaut pas 0. */
function puissanceTexte(watts) {
  if (watts == null) return 'inconnu';
  const n = Number(watts);
  if (!Number.isFinite(n)) return 'inconnu';
  if (Math.abs(n) < 1000) return `${Math.round(n)} W`;
  return `${(n / 1000).toFixed(2).replace('.', ',')} kW`;
}

/**
 * Ce que la machine DECLARE pouvoir faire.
 *
 * A ne pas confondre avec la ligne « Puissance » du bloc au-dessus, qui est ce qu'elle
 * fait a l'instant. Une batterie au repos y affiche 0 kW, et pendant longtemps c'etait
 * le seul chiffre de puissance de tout l'ecran : on pouvait en conclure qu'une machine
 * parfaitement vivante ne valait rien.
 *
 * Or c'est la puissance MISE A DISPOSITION qui se remunere, pas celle qui circule. Elle
 * est annoncee par la machine elle-meme dans ses messages de decouverte, elle arrive
 * depuis toujours sur notre broker, et elle etait jetee.
 *
 * DEUX bornes distinctes et jamais fusionnees : la plage de pilotage (`power_ctrl`,
 * relevee a -1000 / +800 W sur le materiel de recette) et la puissance de sortie
 * configurable (`output_power`, relevee a 100 / 2000 W sur la MEME machine). L'ecart
 * entre les deux n'est pas explique par le fabricant, et le combler par un calcul serait
 * inventer.
 *
 * On ne DEDUIT rien du type de raccordement. Une prise et un tableau ne mettent pas la
 * meme puissance a disposition — c'est un facteur trois sur la note — mais le seul
 * chantier mesure a ce jour est une prise. Ecrire « raccordement au tableau » sur la foi
 * d'un seuil invente donnerait une puissance facturable fausse, et elle se paierait dans
 * les deux sens. L'ecran montre, date, et laisse conclure celui qui sait comment le
 * chantier a ete cable.
 */
function LimitesDeclarees({ limites }) {
  if (!limites) return null;
  const { modulable, sortie } = limites;
  if (!modulable && !sortie) return null;

  return (
    <div
      className="rounded-md border border-neutral-200 bg-neutral-50 px-2.5 py-2 text-[11px]"
      data-testid="battery-limites"
    >
      <p className="mb-1 flex items-center gap-1.5 font-medium text-neutral-800">
        <SlidersHorizontal className="h-3.5 w-3.5 flex-shrink-0 text-neutral-500" />
        Puissance déclarée par la machine
      </p>
      <div className="space-y-0.5">
        {modulable && (
          <LignePlage
            label="Plage de pilotage"
            bornes={modulable}
            tid="limite-modulable"
            aide="Ce que Flexcore peut commander. Négatif = charge."
          />
        )}
        {sortie && (
          <LignePlage
            label="Puissance de sortie"
            bornes={sortie}
            tid="limite-sortie"
            aide="Réglage de sortie annoncé séparément par la machine."
          />
        )}
      </div>
      <p className="mt-1 text-[10px] text-neutral-500">
        Valeurs annoncées par la batterie, pas mesurées. Le type de raccordement
        (prise ou tableau) ne s&apos;en déduit pas : il se lit sur le chantier.
        {limites.vu_a && ` Reçu le ${horodatageTexte(limites.vu_a) || limites.vu_a}.`}
      </p>
    </div>
  );
}

/** Une plage annoncée. Une borne absente s'écrit « non déclaré », jamais 0. */
function LignePlage({ label, bornes, tid, aide }) {
  const { min_w: mini, max_w: maxi, unite } = bornes;
  return (
    <div className="flex items-start justify-between gap-3" data-testid={tid}>
      <span className="text-neutral-500">
        {label}
        <span className="block text-[10px] text-neutral-400">{aide}</span>
      </span>
      <span className="whitespace-nowrap font-medium text-neutral-800">
        {borneTexte(mini, unite)} <span className="text-neutral-400">à</span> {borneTexte(maxi, unite)}
      </span>
    </div>
  );
}

/** Une borne non annoncée n'est pas zéro : c'est une absence, et elle se dit. */
function borneTexte(valeur, unite) {
  if (valeur === null || valeur === undefined) return <span className="italic text-neutral-400">non déclaré</span>;
  const n = Number(valeur);
  if (!Number.isFinite(n)) return <span className="italic text-neutral-400">non déclaré</span>;
  const signe = n > 0 ? '+' : '';
  return `${signe}${Math.round(n)} ${unite || 'W'}`;
}

/**
 * L'alimentation secteur de la machine.
 *
 * C'est une TENSION qui répond, pas une puissance : mesuré sur le matériel le
 * 06/08, 234 V au port secteur alors que 0 W circulait. Une puissance nulle veut
 * dire « rien ne passe », pas « débranché ».
 *
 * Et une batterie survit à sa propre coupure de courant : elle continue de parler
 * sur sa réserve. C'est le seul équipement du parc où la perte de secteur ne se
 * voit PAS par le silence — d'où ce bloc.
 *
 * Ce relevé n'arrive que toutes les 5 minutes, contre une fois par seconde pour la
 * charge. On le date donc séparément plutôt que de laisser croire à du temps réel.
 */
function Alimentation({ secteur }) {
  if (!secteur) return null;
  const { presence, tension_v: tension, frequence_hz: frequence, age_s: age } = secteur;

  if (presence === null || presence === undefined) {
    return (
      <div
        className="flex items-start gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-2.5 py-2 text-[11px] text-neutral-600"
        data-testid="battery-secteur"
      >
        <HelpCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-neutral-400" />
        <span data-testid="secteur-etat">
          Alimentation inconnue — le relevé détaillé n&apos;est pas encore arrivé.
          Il est publié toutes les 5 minutes.
        </span>
      </div>
    );
  }

  const surSecteur = presence === true;
  return (
    <div
      className={`flex items-start gap-2 rounded-md border px-2.5 py-2 text-[11px] ${
        surSecteur
          ? 'border-emerald-100 bg-emerald-50 text-emerald-900'
          : 'border-amber-100 bg-amber-50 text-amber-900'}`}
      data-testid="battery-secteur"
    >
      {surSecteur
        ? <Plug className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-600" />
        : <PlugZap className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600" />}
      <div className="space-y-0.5">
        <p className="font-medium" data-testid="secteur-etat">
          {surSecteur ? 'Sur secteur' : 'Plus de secteur'}
        </p>
        <p className="opacity-80">
          {surSecteur ? (
            <>
              {tension != null && `${Number(tension).toFixed(0)} V`}
              {frequence != null && ` · ${Number(frequence).toFixed(1)} Hz`}
            </>
          ) : (
            "La batterie tourne sur sa réserve. Elle continue de répondre, mais elle se vide."
          )}
        </p>
        {age != null && (
          <p className="text-[10px] opacity-60">Relevé il y a {ancienneteTexte(age)}</p>
        )}
      </div>
    </div>
  );
}

/**
 * Le mode dans lequel la machine se trouve VRAIMENT.
 *
 * Une consigne de charge n'a d'effet que si la machine est passée en mode piloté.
 * Tant qu'on ne lisait pas ce champ, on republiait toutes les 30 s sans jamais
 * pouvoir constater un refus : le 28/07, une consigne a été republiée 240 fois
 * en 2 h sur une machine qui ne l'appliquait pas.
 */
function ModePilotage({ mode }) {
  if (!mode || !mode.libelle) return null;

  // Au-delà du délai d'abandon, le serveur met `pilote_par_flexcore` à null tout
  // en gardant le dernier libellé connu. On ne l'affiche PAS comme un fait :
  // « Mode automatique » sur une machine éteinte depuis 16 minutes est une
  // affirmation qui n'est plus vérifiée, et c'est sur ce champ qu'on décide s'il
  // faut renvoyer une consigne. Constaté en recette le 06/08.
  if (mode.pilote_par_flexcore === null || mode.pilote_par_flexcore === undefined) {
    return (
      <div
        className="flex items-start gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-2.5 py-2 text-[11px] text-neutral-600"
        data-testid="battery-mode-pilotage"
      >
        <HelpCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-neutral-400" />
        <div className="space-y-0.5">
          <p className="font-medium" data-testid="mode-pilotage-libelle">
            Mode de pilotage inconnu
          </p>
          <p className="opacity-80">
            Le relevé détaillé n&apos;est pas assez récent pour l&apos;affirmer.
            {mode.libelle && ` Dernier connu : ${mode.libelle.toLowerCase()}.`}
          </p>
          {mode.age_s != null && (
            <p className="text-[10px] opacity-60">Relevé il y a {ancienneteTexte(mode.age_s)}</p>
          )}
        </div>
      </div>
    );
  }

  const pilote = mode.pilote_par_flexcore === true;
  return (
    <div
      className={`flex items-start gap-2 rounded-md border px-2.5 py-2 text-[11px] ${
        pilote
          ? 'border-violet-100 bg-violet-50 text-violet-900'
          : 'border-neutral-200 bg-neutral-50 text-neutral-700'}`}
      data-testid="battery-mode-pilotage"
    >
      <Radio className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 ${pilote ? 'text-violet-600' : 'text-neutral-400'}`} />
      <div className="space-y-0.5">
        <p className="font-medium" data-testid="mode-pilotage-libelle">{mode.libelle}</p>
        <p className="opacity-80">
          {pilote
            ? "La machine applique bien les consignes envoyées par FlexCore."
            : "La machine gère elle-même sa charge. Une consigne envoyée maintenant la ferait basculer en mode piloté."}
        </p>
        {mode.age_s != null && (
          <p className="text-[10px] opacity-60">Relevé il y a {ancienneteTexte(mode.age_s)}</p>
        )}
      </div>
    </div>
  );
}

/**
 * Les compteurs d'énergie de la machine.
 *
 * Ce sont des CUMULS : ils disent où on en est, pas ce qui s'est passé entre deux
 * instants. Prouver un effacement à un agrégateur demande la différence entre deux
 * relevés, donc un historique — qui n'existe pas encore. Ce bloc est la moitié du
 * chemin, et il vaut mieux le dire que le laisser croire.
 */
function Energie({ energie }) {
  if (!energie) return null;
  const { reseau = {}, batterie = {} } = energie;
  const lignes = [
    ['Soutiré au réseau, aujourd\u2019hui', reseau.jour_entree_wh],
    ['Injecté au réseau, aujourd\u2019hui', reseau.jour_sortie_wh],
    ['Chargé dans la batterie, aujourd\u2019hui', batterie.jour_chargee_wh],
    ['Déchargé de la batterie, aujourd\u2019hui', batterie.jour_dechargee_wh],
  ].filter(([, v]) => v !== null && v !== undefined);

  if (!lignes.length) return null;

  return (
    <div
      className="rounded-md border border-neutral-200 bg-neutral-50 px-2.5 py-2 text-[11px]"
      data-testid="battery-energie"
    >
      <p className="mb-1 flex items-center gap-1.5 font-medium text-neutral-800">
        <Gauge className="h-3.5 w-3.5 flex-shrink-0 text-neutral-500" />
        Énergie
      </p>
      <div className="space-y-0.5">
        {lignes.map(([label, valeur]) => (
          <div key={label} className="flex items-center justify-between">
            <span className="text-neutral-500">{label}</span>
            <span className="font-medium text-neutral-800">{energieTexte(valeur)}</span>
          </div>
        ))}
        {reseau.total_entree_wh != null && (
          <div className="flex items-center justify-between border-t border-neutral-200 pt-0.5">
            <span className="text-neutral-500">Depuis l&apos;origine, soutiré</span>
            <span className="font-medium text-neutral-800">{energieTexte(reseau.total_entree_wh)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/** Des Wh sous le millier, des kWh au-dessus : personne ne lit « 6720 Wh ». */
function energieTexte(wh) {
  if (wh == null) return 'inconnu';
  const n = Number(wh);
  if (!Number.isFinite(n)) return 'inconnu';
  if (Math.abs(n) < 1000) return `${Math.round(n)} Wh`;
  return `${(n / 1000).toFixed(1).replace('.', ',')} kWh`;
}

/**
 * La qualité du lien radio de la machine.
 *
 * C'est la seule mesure du parc qui permette d'agir AVANT la panne : un site dont
 * le signal se dégrade annonce ses déconnexions. On traduit les dBm en un mot,
 * parce que « -65 dBm » ne dit rien à personne.
 */
function Signal({ signal }) {
  if (!signal || !signal.niveau) return null;
  const alerte = signal.niveau === 'faible' || signal.niveau === 'moyen';
  return (
    <div
      className={`flex items-start gap-2 rounded-md border px-2.5 py-2 text-[11px] ${
        alerte
          ? 'border-amber-100 bg-amber-50 text-amber-900'
          : 'border-neutral-200 bg-neutral-50 text-neutral-700'}`}
      data-testid="battery-signal"
    >
      <Wifi className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 ${alerte ? 'text-amber-600' : 'text-neutral-400'}`} />
      <div className="space-y-0.5">
        <p className="font-medium" data-testid="signal-libelle">
          {signal.libelle} <span className="font-normal opacity-60">({signal.dbm} dBm)</span>
        </p>
        <p className="opacity-80">{signal.commentaire}</p>
      </div>
    </div>
  );
}

/** « inconnu » plutôt que « veille » : une batterie sans mesure n'est pas au repos. */
function etatTexte(etat) {
  if (!etat || etat === 'inconnu') return 'inconnu';
  return etat;
}

/**
 * Une valeur grisée dit « ce n'est pas maintenant » sans qu'on ait à le relire.
 * Une valeur inconnue est en italique : elle n'est pas du même ordre qu'un chiffre.
 */
function Chiffre({ label, valeur, perime }) {
  const inconnu = valeur === 'inconnu';
  const ton = inconnu ? 'text-neutral-400 italic'
    : perime ? 'text-neutral-400' : 'text-neutral-800';
  return (
    <div className="flex items-center justify-between text-[11px]">
      <span className="text-neutral-400">{label}</span>
      <span className={`font-medium ${ton}`}>{valeur}</span>
    </div>
  );
}

/**
 * Batterie déclarée pour laquelle aucun contact n'a été enregistré.
 *
 * Ce bloc ne s'affiche plus que dans ce seul cas. Une batterie simplement déconnectée
 * garde ses chiffres et son ancienneté : la date du dernier contact est lue en base, un
 * redémarrage du serveur ne la fait plus retomber ici.
 *
 * Le texte reste volontairement à ce niveau : la liaison de la batterie est posée en
 * usine par le fournisseur, l'installateur ne saisit que le numéro fourni par le
 * fabricant. Aucun paramètre, aucun identifiant, aucun mot de passe n'a donc à
 * apparaître ici — il n'y a rien à corriger soi-même sur cet écran.
 */
function SansContact() {
  return (
    <div
      className="flex items-start gap-2 p-3 rounded-md bg-neutral-50 border border-neutral-200 text-[11px] text-neutral-600"
      data-testid="battery-never-seen"
    >
      <HelpCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-neutral-400" />
      <div className="space-y-1">
        <p className="font-medium text-neutral-800">
          Aucun contact enregistré pour cette batterie
        </p>
        <p>
          La batterie est bien déclarée dans Flexcore, mais aucune mesure n'a été reçue
          d'elle à ce jour. Deux explications possibles : elle n'est pas alimentée, ou sa
          préparation d'usine est absente ou erronée.
        </p>
        <p>
          Aucun réglage n'est à faire depuis cet écran :
          <strong> signalez la batterie à un administrateur</strong>, qui fera le point avec
          le fournisseur.
        </p>
      </div>
    </div>
  );
}
